import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, LogIn, Eye, EyeOff, MailCheck, KeyRound, UserRound } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../ui/input-otp';
import { toast } from 'sonner';
import { publicPost, storeClientSession, errorMessage, AGENCY_PHONE } from '../../lib/clientApi';
import { ContactAgencyButton } from './shared';

const LOGO = 'https://customer-assets.emergentagent.com/job_easers-pro/artifacts/j38s08lp_LOGOSITE.png';

function PasswordInput({ id, value, onChange, autoComplete, testId, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input id={id} type={show ? 'text' : 'password'} autoComplete={autoComplete} value={value} onChange={(e) => onChange(e.target.value)} className="h-12 text-base pr-12" placeholder={placeholder} data-testid={testId} />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground" aria-label={show ? 'Masquer le mot de passe' : 'Afficher le mot de passe'} data-testid={`${testId}-toggle`}>
        {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
      </button>
    </div>
  );
}

/**
 * Client authentication: login, first access (e-mail check -> code -> password) and password reset.
 * The e-mail must be the main e-mail of a Dolibarr customer; otherwise we invite to call the agency.
 */
export function ClientAuth({ onLogin }) {
  const [mode, setMode] = useState('login'); // login | signup | reset
  const [step, setStep] = useState('email'); // email | code | unknown
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [codeInfo, setCodeInfo] = useState(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const switchMode = (m) => { setMode(m); setStep('email'); setCode(''); setPassword(''); setConfirm(''); setCodeInfo(null); };

  const finish = (data, greeting) => {
    storeClientSession(data.token, data.client);
    toast.success(greeting);
    onLogin(data.client);
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await publicPost('/api/client/login', { email: email.trim(), password });
      finish(res.data, `Bonjour ${res.data.client.name} !`);
    } catch (err) {
      toast.error(errorMessage(err, 'Connexion impossible'));
    } finally { setLoading(false); }
  };

  const requestCode = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const check = await publicPost('/api/client/check-email', { email: email.trim() });
      if (!check.data.exists) { setStep('unknown'); return; }
      setName(check.data.name || '');
      if (mode === 'signup' && check.data.has_account) {
        toast.info('Un mot de passe existe déjà pour cet e-mail : connectez-vous ou utilisez « Mot de passe oublié ».');
        setMode('login');
        return;
      }
      if (mode === 'reset' && !check.data.has_account) {
        toast.info("Aucun mot de passe n'a encore été créé : passez par « Première connexion ».");
        setMode('signup');
        return;
      }
      const res = await publicPost('/api/client/request-code', { email: email.trim(), purpose: mode === 'reset' ? 'reset' : 'signup' });
      setCodeInfo(res.data);
      setStep('code');
      if (res.data.sent) toast.success('Code envoyé par e-mail'); else toast.warning(res.data.message);
    } catch (err) {
      if (err?.response?.status === 404) setStep('unknown'); else toast.error(errorMessage(err));
    } finally { setLoading(false); }
  };

  const setNewPassword = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error('Le mot de passe doit contenir au moins 8 caractères');
    if (password !== confirm) return toast.error('Les deux mots de passe ne correspondent pas');
    setLoading(true);
    try {
      const res = await publicPost('/api/client/set-password', { email: email.trim(), code, password });
      finish(res.data, mode === 'reset' ? 'Mot de passe mis à jour' : `Bienvenue ${res.data.client.name} !`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally { setLoading(false); }
  };

  const title = mode === 'login' ? 'Connexion' : mode === 'signup' ? 'Première connexion' : 'Mot de passe oublié';
  const prefix = mode === 'login' ? 'login' : mode === 'signup' ? 'first-access' : 'forgot';

  return (
    <div className="min-h-screen flex flex-col bg-[hsl(var(--secondary))]/40" data-testid="client-auth-page">
      <div className="h-1 w-full bg-[hsl(var(--brand-apricot))]" aria-hidden="true" />
      <div className="max-w-md w-full mx-auto px-4 pt-6 pb-10 flex-1 flex flex-col">
        <Link to="/" className="inline-flex items-center gap-1 text-base text-muted-foreground hover:text-foreground mb-6 min-h-[44px]" data-testid="client-back-home">
          <ArrowLeft className="h-4 w-4" /> Retour au site
        </Link>
        <div className="flex flex-col items-center text-center mb-6">
          <img src={LOGO} alt="Ptitservices.com" className="h-14 w-auto rounded-xl mb-4" />
          <p className="text-xs uppercase tracking-wide text-[hsl(var(--brand-apricot))] font-semibold">Espace client</p>
          <h1 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight mt-1" data-testid="client-auth-title">{title}</h1>
          <p className="text-base text-muted-foreground mt-1">Factures, interventions, avis et réclamations.</p>
        </div>

        <Card className="p-6 shadow-lg border-border">
          {mode === 'login' && (
            <form onSubmit={submitLogin} className="space-y-4" data-testid="client-login-form">
              <p className="text-base text-muted-foreground">Connectez-vous avec l'adresse e-mail utilisée chez Ptitservices.</p>
              <div className="space-y-1.5">
                <Label htmlFor="client-email" className="text-base">E-mail</Label>
                <Input id="client-email" type="email" inputMode="email" autoComplete="username" autoCapitalize="none" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 text-base" placeholder="vous@exemple.fr" data-testid="client-login-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="client-password" className="text-base">Mot de passe</Label>
                <PasswordInput id="client-password" value={password} onChange={setPassword} autoComplete="current-password" testId="client-login-password-input" />
              </div>
              <Button type="submit" size="lg" className="w-full h-12 text-base" disabled={loading || !email.trim() || !password} data-testid="client-login-submit-button">
                {loading ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <LogIn className="h-5 w-5 mr-2" />} Se connecter
              </Button>
              <div className="flex flex-col gap-1 pt-1">
                <button type="button" className="text-base text-[hsl(var(--primary))] underline-offset-4 hover:underline min-h-[44px]" onClick={() => switchMode('signup')} data-testid="client-login-first-access-link">Première connexion ? Créer mon mot de passe</button>
                <button type="button" className="text-base text-muted-foreground underline-offset-4 hover:underline min-h-[44px]" onClick={() => switchMode('reset')} data-testid="client-login-forgot-password-link">Mot de passe oublié</button>
              </div>
            </form>
          )}

          {mode !== 'login' && step === 'email' && (
            <form onSubmit={requestCode} className="space-y-4" data-testid={`client-${prefix}-email-form`}>
              <ol className="text-base text-muted-foreground space-y-1 list-decimal pl-5">
                <li>Saisissez votre e-mail</li><li>Recevez un code de vérification</li><li>{mode === 'reset' ? 'Choisissez un nouveau mot de passe' : 'Créez votre mot de passe'}</li>
              </ol>
              <div className="space-y-1.5">
                <Label htmlFor="client-email-2" className="text-base">E-mail connu de Ptitservices</Label>
                <Input id="client-email-2" type="email" inputMode="email" autoComplete="username" autoCapitalize="none" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 text-base" placeholder="vous@exemple.fr" data-testid={`client-${prefix}-email-input`} />
              </div>
              <Button type="submit" size="lg" className="w-full h-12 text-base" disabled={loading || !email.trim()} data-testid={`client-${prefix}-send-code-button`}>
                {loading ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <MailCheck className="h-5 w-5 mr-2" />} Recevoir mon code
              </Button>
              <button type="button" className="w-full text-base text-muted-foreground underline-offset-4 hover:underline min-h-[44px]" onClick={() => switchMode('login')} data-testid="client-auth-back-to-login">J'ai déjà un mot de passe</button>
            </form>
          )}

          {mode !== 'login' && step === 'unknown' && (
            <div className="space-y-4 text-center" data-testid="client-unknown-email">
              <UserRound className="h-10 w-10 mx-auto text-muted-foreground" />
              <p className="text-base leading-relaxed">Nous ne trouvons pas <strong>{email}</strong> dans nos dossiers. L'espace client est réservé aux clients Ptitservices : contactez l'agence pour activer votre accès.</p>
              <ContactAgencyButton variant="default" testId="client-unknown-email-contact-button" className="w-full" />
              <button type="button" className="text-base text-muted-foreground underline-offset-4 hover:underline min-h-[44px]" onClick={() => setStep('email')} data-testid="client-unknown-email-retry">Essayer une autre adresse</button>
            </div>
          )}

          {mode !== 'login' && step === 'code' && (
            <form onSubmit={setNewPassword} className="space-y-5" data-testid={`client-${prefix}-code-form`}>
              <div className="rounded-lg bg-secondary/70 px-3 py-2 text-base" role="status" data-testid="client-code-info">
                {name && <p className="font-semibold">{name}</p>}
                <p className="text-muted-foreground">{codeInfo?.sent ? `Un code à 6 chiffres a été envoyé à ${email}. Il reste valable ${codeInfo.expires_in_min} minutes.` : `L'envoi automatique est indisponible : appelez l'agence au ${AGENCY_PHONE}, elle vous communiquera votre code.`}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-base">Code de vérification</Label>
                <InputOTP maxLength={6} value={code} onChange={setCode} containerClassName="justify-center" data-testid={`client-${prefix}-otp-input`}>
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="client-new-password" className="text-base">{mode === 'reset' ? 'Nouveau mot de passe' : 'Votre mot de passe'} (8 caractères minimum)</Label>
                <PasswordInput id="client-new-password" value={password} onChange={setPassword} autoComplete="new-password" testId={`client-${prefix}-${mode === 'reset' ? 'new-password' : 'password'}-input`} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="client-confirm-password" className="text-base">Confirmez le mot de passe</Label>
                <PasswordInput id="client-confirm-password" value={confirm} onChange={setConfirm} autoComplete="new-password" testId={`client-${prefix}-confirm-password-input`} />
              </div>
              <Button type="submit" size="lg" className="w-full h-12 text-base" disabled={loading || code.length !== 6 || password.length < 8 || !confirm} data-testid={`client-${prefix}-confirm-button`}>
                {loading ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <KeyRound className="h-5 w-5 mr-2" />} {mode === 'reset' ? 'Changer mon mot de passe' : 'Créer mon mot de passe'}
              </Button>
              <button type="button" className="w-full text-base text-muted-foreground underline-offset-4 hover:underline min-h-[44px]" onClick={requestCode} disabled={loading} data-testid="client-code-resend">Renvoyer un code</button>
            </form>
          )}
        </Card>

        <p className="text-sm text-muted-foreground text-center mt-6 leading-relaxed">
          Accès réservé aux clients Ptitservices. Une question ? Appelez l'agence au {AGENCY_PHONE}.
        </p>
      </div>
    </div>
  );
}
